
/**
 * Returns average milk intake by age in months (ml)
 * Based on general pediatric guidelines
 * @param ageMonths Baby's age in months
 * @returns Average milk intake in ml
 */
export const averageMilkIntakeByAge = (ageMonths: number): number => {
  // Average milk intake in ml by age (general guidelines)
  if (ageMonths < 1) {
    // Newborns (0-1 month): ~60ml per feeding, 8 feedings = ~480ml/day
    return 480;
  } else if (ageMonths < 2) {
    // 1-2 months: ~90ml per feeding, 7-8 feedings = ~630ml/day
    return 630;
  } else if (ageMonths < 4) {
    // 2-4 months: ~120ml per feeding, 6-7 feedings = ~780ml/day
    return 780;
  } else if (ageMonths < 6) {
    // 4-6 months: ~150ml per feeding, 5-6 feedings = ~825ml/day
    return 825;
  } else if (ageMonths < 9) {
    // 6-9 months: ~180ml per feeding, 4-5 feedings = ~810ml/day
    // Complementary foods also introduced
    return 810;
  } else if (ageMonths < 12) {
    // 9-12 months: ~210ml per feeding, 3-4 feedings = ~735ml/day
    // More solid foods in diet
    return 735;
  } else if (ageMonths < 18) {
    // 12-18 months: ~240ml per feeding, 2-3 feedings = ~600ml/day
    return 600;
  } else if (ageMonths < 24) {
    // 18-24 months: ~240ml per feeding, 2 feedings = ~480ml/day
    return 480;
  } else {
    // 2+ years: ~240ml per feeding, 1-2 feedings = ~360ml/day
    return 360;
  }
};

/**
 * Converts milk intake from different formats to a number
 * @param milkIntakeStr Milk intake string from diary entry
 * @returns Milk intake as number in ml
 */
export const extractMilkIntakeValue = (milkIntakeStr: string | undefined): number | null => {
  if (!milkIntakeStr) return null;
  
  const mlRegex = /(\d+).*?ml/i;
  const ozRegex = /(\d+(?:\.\d+)?).*?oz/i;
  
  // Try to match ml format
  let match = milkIntakeStr.match(mlRegex);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  
  // Try to match oz format and convert to ml
  match = milkIntakeStr.match(ozRegex);
  if (match && match[1]) {
    const oz = parseFloat(match[1]);
    return Math.round(oz * 29.5735); // Convert oz to ml
  }
  
  // If no units found, try to extract just the number
  const numericMatch = milkIntakeStr.match(/(\d+)/);
  if (numericMatch && numericMatch[1]) {
    // Assume ml if no units specified
    return parseInt(numericMatch[1], 10);
  }
  
  return null;
};

/**
 * Format milk intake value for display with proper units
 * @param value Milk intake value in ml
 * @param useMetric Whether to use metric (ml) or imperial (oz)
 * @returns Formatted milk intake string with units
 */
export const formatMilkIntakeForDisplay = (value: number, useMetric: boolean): string => {
  if (useMetric) {
    return `${value} ml`;
  } else {
    // Convert ml to oz (1 ml = 0.033814 oz)
    const oz = value * 0.033814;
    return `${oz.toFixed(1)} oz`;
  }
};
